/**
 * The operator console. Admin role only.
 *
 * It exists because of 14 Aug 2026: Lee deployed with an ingest, a registry
 * entry and zero rows, and every page in the product said the data was fresh —
 * because "last pull" was a property of the run and one county's success spoke
 * for all four. Counties are pulled separately now, so the per-county view is
 * the one that matters, and there was nowhere to see it whole.
 *
 * Two rules this page follows:
 *
 * 1. **Nothing here is a gate.** The client hiding the link is presentation;
 *    `require_admin` on the backend router is what refuses everyone else, on
 *    every endpoint behind it. If this file were deleted the console would be
 *    exactly as private.
 * 2. **A county with no rows must never read as healthy.** That is the failure
 *    this page was built for, and `lib/admin.ts` holds the wording with tests
 *    on it rather than deciding it inline here.
 */

import { useCallback, useEffect, useState } from "react";

import { ApiError, api } from "../api/client";
import type { AdminOverview, AdminUser, RefreshRun } from "../api/types";
import {
  ago,
  answeredNothing,
  feedNotes,
  health,
  refusalNote,
  verdict,
} from "../lib/admin";
import { describePull, pullBusy, watchNote } from "../lib/pullProgress";
import { countyName } from "../lib/counties";
import { fmt, timestamp } from "../lib/format";

export function AdminPage() {
  const [overview, setOverview] = useState<AdminOverview | null>(null);
  const [users, setUsers] = useState<AdminUser[] | null>(null);
  const [userTotal, setUserTotal] = useState(0);
  const [runs, setRuns] = useState<RefreshRun[]>([]);
  const [error, setError] = useState("");
  const [note, setNote] = useState("");
  const [pulling, setPulling] = useState("");

  const load = useCallback(async (signal?: AbortSignal) => {
    try {
      const [health, roster, history] = await Promise.all([
        api.adminOverview(signal),
        api.adminUsers(100, signal),
        api.adminHistory(undefined, 20, signal),
      ]);
      setOverview(health);
      setUsers(roster.users);
      setUserTotal(roster.total);
      setRuns(history.runs);
      setError("");
    } catch (cause) {
      // "I cancelled this" is not "the network is down" — the distinction the
      // whole client keeps, and the reason AbortError is re-thrown untouched
      // everywhere else.
      if (cause instanceof DOMException && cause.name === "AbortError") return;
      setError(
        cause instanceof ApiError ? cause.message : "The console is unavailable.",
      );
    }
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    void load(controller.signal);
    return () => controller.abort();
  }, [load]);

  // A pull is minutes long and runs on a background thread, so the page polls
  // rather than pretending the button finished the work. Only while something
  // is actually in flight: an idle console should not sit there re-querying.
  //
  // "In flight" is the engine's pull, this app's copy after it, **or** an
  // operator's pull being watched. It read this app's copy alone until
  // 28 Sep 2026, so pressing Pull left a page that never polled: the engine
  // pulls for minutes and the copy that would have set `running` came later.
  const busy = pullBusy(overview);
  useEffect(() => {
    if (!busy) return;
    const timer = window.setInterval(() => void load(), 5000);
    return () => window.clearInterval(timer);
  }, [busy, load]);

  async function pull(county?: string, acceptDrop = false) {
    if (acceptDrop && county) {
      // Replacing a county's rows with far fewer is the one thing on this page
      // that cannot be taken back by pulling again tomorrow if the source was
      // broken — so it is asked, with the numbers.
      const refusal = overview?.counties.find((c) => c.fips === county)
        ?.engine_refusal;
      const what = refusal
        ? `${refusal.held_buildings.toLocaleString()} buildings with ` +
          `${(refusal.buildings ?? 0).toLocaleString()}`
        : "what it holds with a much smaller answer";
      if (!window.confirm(`Replace ${countyName(county)}'s ${what}?`)) return;
    }
    setPulling(county ?? "all");
    setNote("");
    setError("");
    try {
      const result = await api.adminRefresh(county, acceptDrop);
      setNote(
        result.note ??
          (county
            ? `Pulling ${countyName(county)}.`
            : "Pulling every county that is due."),
      );
      await load();
    } catch (cause) {
      setError(
        cause instanceof ApiError ? cause.message : "Could not start a pull.",
      );
    } finally {
      setPulling("");
    }
  }

  if (error && !overview) {
    return (
      <main className="ops">
        <h1>Operations</h1>
        <div className="notice error">{error}</div>
      </main>
    );
  }

  if (!overview) {
    return (
      <main className="ops">
        <div className="eyebrow">Loading</div>
      </main>
    );
  }

  return (
    <AdminConsole
      overview={overview}
      users={users}
      userTotal={userTotal}
      runs={runs}
      error={error}
      note={note}
      pulling={pulling}
      onPull={pull}
    />
  );
}

export interface AdminConsoleProps {
  overview: AdminOverview;
  users: AdminUser[] | null;
  userTotal: number;
  runs: RefreshRun[];
  error?: string;
  note?: string;
  /** The county whose Pull button was pressed, "all", or "" when idle. */
  pulling?: string;
  /** `acceptDrop` lets one county through the engine's collapse guard. */
  onPull: (county?: string, acceptDrop?: boolean) => void;
}

/**
 * The console itself, pure over its props.
 *
 * Split from the container so it can be rendered in a test with no DOM and no
 * network — this project has no jsdom and no testing-library, and one page is
 * not worth adding them for. What the test is actually protecting is the copy:
 * a county holding nothing must say so on screen, not merely in a function
 * `admin.test.ts` calls directly.
 */
export function AdminConsole({
  overview,
  users,
  userTotal,
  runs,
  error = "",
  note = "",
  pulling = "",
  onPull,
}: AdminConsoleProps) {
  const { counties, accounts, refresh } = overview;

  return (
    <main className="ops">
      <h1>Operations</h1>
      <p className="sub">{verdict(counties)}</p>

      {error && <div className="notice error">{error}</div>}
      {note && <div className="notice">{note}</div>}

      {/* ---------------------------------------------------------------- */}
      <section className="opssec">
        <div className="opshead">
          <h2>Counties</h2>
          <button
            className="linkbtn"
            disabled={Boolean(pulling)}
            onClick={() => onPull()}
          >
            {pulling === "all" ? "Starting…" : "Pull everything due"}
          </button>
        </div>
        <PullProgress overview={overview} />

        {/* In the order the sweep will take them, not alphabetically: the
            county at the top is the one that goes next, and a list sorted any
            other way implies a queue that does not exist. */}
        <p className="sub tiny">
          In pull order — furthest behind first, and a county we have never held
          before any we have.
        </p>

        <div className="opstable">
          <table>
            <thead>
              <tr>
                <th>County</th>
                <th>State</th>
                <th className="num">Parcels</th>
                <th className="num">Buildings</th>
                <th>Last success</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {counties.map((county) => {
                const state = health(county);
                return (
                  <tr key={county.fips}>
                    <td>
                      <b>{county.name}</b>
                      <div className="mono tiny">{county.fips}</div>
                    </td>
                    <td>
                      <span className={`badge ${state.tone}`}>{state.label}</span>
                      {state.detail && (
                        <div className="sub tiny">{state.detail}</div>
                      )}
                      {county.last_error && (
                        <div className="sub tiny err">{county.last_error}</div>
                      )}
                      {county.engine_refusal && (
                        <div className="sub tiny err">{refusalNote(county)}</div>
                      )}
                      {/* A frozen feed passes every other check here: the
                          pull is current and the data is not. */}
                      {feedNotes(county).map((note) => (
                        <div
                          key={note.label}
                          className={`sub tiny ${note.tone}`}
                        >
                          {note.text}
                        </div>
                      ))}
                    </td>
                    <td className="num">{fmt(county.unit_count)}</td>
                    <td className="num">{fmt(county.building_count)}</td>
                    <td>
                      {county.finished_at ? (
                        <>
                          <div>{timestamp(county.finished_at)}</div>
                          <div className="sub tiny">
                            {ago(county.finished_at)}
                          </div>
                        </>
                      ) : (
                        /* Never a timestamp borrowed from another county —
                           that substitution is the bug this page reports on. */
                        <span className="sub">—</span>
                      )}
                    </td>
                    <td>
                      <button
                        className="linkbtn"
                        disabled={Boolean(pulling) || county.running}
                        onClick={() => onPull(county.fips)}
                      >
                        {pulling === county.fips ? "Starting…" : "Pull"}
                      </button>
                      {/* Only where the engine refused: the operator's word
                          that the county really is that much smaller. Never
                          for an answer of nothing — that is the county's
                          outage, and accepting it would delete the county. */}
                      {county.engine_refusal && !answeredNothing(county) && (
                        <div>
                          <button
                            className="linkbtn"
                            disabled={Boolean(pulling) || county.running}
                            onClick={() => onPull(county.fips, true)}
                          >
                            Accept smaller
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>

      {/* ---------------------------------------------------------------- */}
      <section className="opssec">
        <h2>Accounts</h2>
        <div className="statrow">
          <Stat label="Total" value={fmt(accounts.total)} />
          <Stat label="Preview" value={fmt(accounts.by_role.free)} />
          <Stat label="County" value={fmt(accounts.by_role.county)} />
          <Stat label="State" value={fmt(accounts.by_role.state)} />
          <Stat label="Admin" value={fmt(accounts.by_role.admin)} />
          <Stat
            label="Awaiting agreement"
            value={fmt(accounts.awaiting_agreement)}
          />
        </div>
        <p className="sub tiny">
          Current agreement {accounts.agreement_version}. Accounts on an older
          version are prompted on next use — never backfilled, which would
          manufacture evidence of an agreement they were not shown.
        </p>

        <div className="opstable">
          <table>
            <thead>
              <tr>
                <th>Email</th>
                <th>Role</th>
                <th>Stored tier</th>
                <th>Subscription</th>
                <th>Agreement</th>
                <th>Last seen</th>
              </tr>
            </thead>
            <tbody>
              {(users ?? []).map((user) => (
                <tr key={user.id}>
                  <td className="mono">{user.email}</td>
                  <td>
                    <span
                      className={`badge ${
                        user.role === "admin" ? "b-amber" : "b-grey"
                      }`}
                    >
                      {user.role_name}
                    </span>
                  </td>
                  {/* Shown beside the role because for an administrator the
                      two differ on purpose: the column stays `free` so no
                      sellable seat is consumed. Hiding it would make this page
                      and `python -m app.cli list-users` appear to disagree. */}
                  <td className="mono tiny">{user.tier}</td>
                  <td className="mono tiny">
                    {user.subscription_status ?? "—"}
                  </td>
                  <td className="mono tiny">{user.agreement_version ?? "—"}</td>
                  <td className="sub tiny">
                    {timestamp(user.last_login) ?? "never"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {users && userTotal > users.length && (
          <p className="sub tiny">
            Showing {fmt(users.length)} of {fmt(userTotal)}.
          </p>
        )}
      </section>

      {/* ---------------------------------------------------------------- */}
      <section className="opssec">
        <h2>Refresh</h2>
        {/* Every one of these is an environment variable that can be right in
            one deployment and silently absent in another — and the symptom of
            a missing FF_REFRESH_AT or FF_ENGINE_API_KEY is that nothing ever
            happens. */}
        <dl className="settings">
          <Setting label="Appointment">
            {refresh.refresh_at
              ? `${refresh.refresh_at} ${refresh.refresh_tz}`
              : `none — age threshold of ${refresh.refresh_hours}h`}
          </Setting>
          <Setting label="Sync on boot">
            {refresh.refresh_on_boot ? "yes" : "no"}
          </Setting>
          <Setting label="Signal Engine">
            {refresh.engine_configured
              ? refresh.engine_url
              : "not configured — nothing syncs"}
          </Setting>
          <Setting label="Scoring catalog">
            {refresh.catalog_version
              ? `engine ${refresh.catalog_version}`
              : "bundled snapshot"}
          </Setting>
          <Setting label="Mode">{refresh.mode}</Setting>
          <Setting label="Next up">
            {refresh.pull_order.map((fips) => countyName(fips)).join(" → ")}
          </Setting>
        </dl>

        <h3>Recent runs</h3>
        <p className="sub tiny">
          One row per county per run. A sweep in which three counties answered
          and one did not is three successes and a failure, not one ambiguous
          result.
        </p>
        <div className="opstable">
          <table>
            <thead>
              <tr>
                <th>County</th>
                <th>Finished</th>
                <th>Result</th>
                <th className="num">Parcels</th>
                <th className="num">Buildings</th>
                <th>Note</th>
              </tr>
            </thead>
            <tbody>
              {runs.map((run) => (
                <tr key={run.id}>
                  <td>
                    {run.county_fips ? (
                      countyName(run.county_fips)
                    ) : (
                      /* Rows written before the log was per-county. Never
                         backfilled: those runs covered every county, and
                         stamping one code onto them would invent a history. */
                      <span className="sub">all (legacy)</span>
                    )}
                  </td>
                  <td className="sub tiny">{timestamp(run.finished_at) ?? "—"}</td>
                  <td>
                    <span className={`badge ${run.ok ? "b-grey" : "b-red"}`}>
                      {run.ok ? "ok" : "failed"}
                    </span>
                  </td>
                  <td className="num">{fmt(run.unit_count)}</td>
                  <td className="num">{fmt(run.bldg_count)}</td>
                  <td className="sub tiny wrap">{run.note}</td>
                </tr>
              ))}
              {runs.length === 0 && (
                <tr>
                  <td colSpan={6} className="sub">
                    No pull has been logged yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>
    </main>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="stat">
      <div className="statv">{value}</div>
      <div className="statl">{label}</div>
    </div>
  );
}

function Setting({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <>
      <dt>{label}</dt>
      <dd>{children}</dd>
    </>
  );
}

/**
 * The engine's pulls under way, one bar each, above the county table.
 *
 * Nothing at all when idle: a console that always shows a bar at 0% trains the
 * eye to skip it. The wording is `lib/pullProgress.ts`'s, with tests.
 */
function PullProgress({ overview }: { overview: AdminOverview }) {
  const note = watchNote(overview);
  if (overview.pulls.runs.length === 0 && !note) return null;
  return (
    <div className="pullprog" aria-live="polite">
      {overview.pulls.runs.map((run, i) => {
        const pull = describePull(run);
        return (
          <div key={`${run.kind}-${run.started_at ?? i}`}>
            <div className="pullhead">
              <b>{pull.title}</b>
              <span className="mono tiny">{pull.percent}%</span>
            </div>
            <div
              className={`pullbar${run.overrun ? " is-long" : ""}`}
              role="progressbar"
              aria-valuemin={0}
              aria-valuemax={100}
              aria-valuenow={pull.percent}
              aria-label={pull.title}
            >
              <span style={{ width: `${pull.percent}%` }} />
            </div>
            <div className="sub tiny">{pull.line}</div>
            {pull.failed && <div className="sub tiny err">{pull.failed}</div>}
          </div>
        );
      })}
      {note && <div className="sub tiny">{note}</div>}
    </div>
  );
}
